import { useState } from 'react';
import { Badge, Card, EmptyState, ErrorMessage, Field, Loading, Meter, Modal, SearchSelect, StatCard, Toggle, useAsync, useToast } from '../../components/ui';
import { IconAlert, IconBox, IconCheck, IconPlus, IconTrash } from '../../components/icons';
import { api } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { formatCurrency, formatDateTime, formatNumber } from '../../lib/format';
import { printList, printQuantity } from '../../lib/print';

/**
 * Inventur (Kap. 7).
 *
 * Eine Inventur ist ein Vorgang, kein Einzelklick: Zählliste erzeugen, Mengen
 * erfassen, Differenzen prüfen, abschließen. Erst der Abschluss schreibt
 * Bestandsbewegungen – bis dahin lässt sich jede Zahl korrigieren.
 */
export function InventoryPanel({ warehouses }: { warehouses: any[] }) {
  const { can } = useAuth();
  const { notify } = useToast();
  const [creating, setCreating] = useState(false);
  const [openId, setOpenId] = useState<number | null>(null);

  const counts = useAsync(() => api.get('/api/stocktaking/counts'), []);
  const mayCount = can('material.inventory');

  const list: any[] = counts.data ?? [];
  const running = list.filter((entry) => entry.status === 'open');

  const remove = async (count: any) => {
    if (!window.confirm(`Inventur „${count.name}“ verwerfen? Erfasste Zählungen gehen verloren.`)) return;
    await api.delete(`/api/stocktaking/counts/${count.id}`);
    notify('Inventur verworfen.', 'success');
    counts.reload();
  };

  if (counts.loading) return <Loading />;

  return (
    <div className="stack">
      <div className="grid cols-3">
        <StatCard
          label="Laufende Inventuren"
          value={running.length}
          tone={running.length > 0 ? 'warning' : 'success'}
          icon={<IconBox width={17} height={17} />}
          hint={running.length > 0 ? 'noch nicht abgeschlossen' : 'alles abgeschlossen'}
        />
        <StatCard
          label="Abgeschlossen"
          value={list.filter((entry) => entry.status === 'closed').length}
          tone="info"
          icon={<IconCheck width={17} height={17} />}
        />
        <StatCard
          label="Offene Differenzen"
          value={running.reduce((sum, entry) => sum + (entry.difference_count ?? 0), 0)}
          tone="accent"
          icon={<IconAlert width={17} height={17} />}
          hint="in laufenden Zählungen"
        />
      </div>

      <div className="row between">
        <span className="section-title">Inventuren</span>
        {mayCount && (
          <button className="btn sm primary" onClick={() => setCreating(true)}>
            <IconPlus width={14} height={14} /> Inventur starten
          </button>
        )}
      </div>

      {list.length === 0 ? (
        <EmptyState
          icon={<IconBox width={20} height={20} />}
          title="Noch keine Inventur"
          hint="Eine Inventur erzeugt eine Zählliste je Lager und bucht am Ende nur die Differenzen."
        />
      ) : (
        <div className="stack" style={{ gap: 10 }}>
          {list.map((count) => (
            <div key={count.id} className={`order-card ${count.status === 'open' ? 'tone-warning' : 'tone-success'}`}>
              <div className="row between">
                <div className="row" style={{ gap: 8 }}>
                  <Badge variant={count.status === 'open' ? 'warning' : 'success'}>
                    {count.status === 'open' ? 'Läuft' : 'Abgeschlossen'}
                  </Badge>
                  <span className="chip">{count.warehouse_name}</span>
                </div>
                <span className="small subtle">{formatDateTime(count.started_at)}</span>
              </div>

              <div className="order-card-title">{count.name}</div>
              <div className="small subtle">
                {count.counted_count} von {count.item_count} gezählt · {count.difference_count} Differenz(en)
                {count.first_name ? ` · begonnen von ${count.first_name} ${count.last_name}` : ''}
              </div>

              <div style={{ marginTop: 10 }}>
                <Meter
                  value={count.counted_count}
                  max={count.item_count || 1}
                  tone={count.counted_count === count.item_count ? 'success' : undefined}
                />
              </div>

              <div className="row" style={{ marginTop: 12 }}>
                <button className="btn sm" onClick={() => setOpenId(count.id)}>
                  {count.status === 'open' ? 'Zählliste öffnen' : 'Ergebnis ansehen'}
                </button>
                <span className="spacer" />
                {mayCount && count.status === 'open' && (
                  <button className="btn sm ghost" onClick={() => remove(count)} title="Inventur verwerfen">
                    <IconTrash width={13} height={13} />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {creating && (
        <StartCountModal
          warehouses={warehouses}
          onClose={() => setCreating(false)}
          onStarted={(id) => {
            setCreating(false);
            counts.reload();
            setOpenId(id);
            notify('Inventur gestartet – Zählliste erzeugt.', 'success');
          }}
        />
      )}

      {openId && (
        <CountSheetModal
          countId={openId}
          canCount={mayCount}
          onClose={() => setOpenId(null)}
          onChanged={() => counts.reload()}
        />
      )}
    </div>
  );
}

function StartCountModal({
  warehouses,
  onClose,
  onStarted,
}: {
  warehouses: any[];
  onClose: () => void;
  onStarted: (id: number) => void;
}) {
  const [warehouseId, setWarehouseId] = useState<string>(String(warehouses[0]?.id ?? ''));
  const [name, setName] = useState('');
  const [includeEmpty, setIncludeEmpty] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  const start = async () => {
    setBusy(true);
    setError(null);
    try {
      const result = await api.post('/api/stocktaking/counts', {
        warehouseId: Number(warehouseId),
        name: name || undefined,
        includeEmpty,
      });
      onStarted(result.id);
    } catch (caught) {
      setError(caught);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      title="Inventur starten"
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            Abbrechen
          </button>
          <button className="btn primary" onClick={start} disabled={busy || !warehouseId}>
            Zählliste erzeugen
          </button>
        </>
      }
    >
      <div className="stack">
        <ErrorMessage error={error} />
        <Field label="Lager" required>
          <SearchSelect
            value={String(warehouseId ?? '')}
            onChange={(wert) => setWarehouseId(wert)}
            options={(warehouses ?? []).map((warehouse: any) => ({ value: String(warehouse.id), label: String(warehouse.name) }))}
            clearable={false}
          />
        </Field>
        <Field label="Bezeichnung" hint="Leer lassen für einen Vorschlag aus Lager und Datum.">
          <input value={name} onChange={(event) => setName(event.target.value)} placeholder="z. B. Jahresinventur 2026" />
        </Field>
        <Toggle
          checked={includeEmpty}
          onChange={setIncludeEmpty}
          label="Auch Artikel ohne Bestand aufnehmen (Vollinventur)"
        />
        <div className="note-box tone-info">
          <IconBox width={15} height={15} />
          <span>
            Der Sollbestand wird beim Erzeugen eingefroren. Läuft während der Zählung eine Entnahme, verfälscht sie
            die Differenz nicht.
          </span>
        </div>
      </div>
    </Modal>
  );
}

/**
 * Zählliste für den Gang durchs Lager.
 *
 * Die Spalte „Gezählt“ bleibt leer – gezählt wird am Regal mit dem Stift, die
 * Zahlen wandern danach in die Maske. Der Sollbestand steht bewusst mit auf
 * dem Blatt: Ihn wegzulassen („damit unvoreingenommen gezählt wird“) führt in
 * der Praxis nur dazu, dass niemand merkt, wenn eine Position vergessen wurde.
 */
function printCountSheet(count: any) {
  printList({
    title: 'Inventur – Zählliste',
    subtitle: count.name,
    meta: [
      { label: 'Lager', value: count.warehouse_name },
      { label: 'Begonnen', value: formatDateTime(count.started_at) },
      { label: 'Positionen', value: String(count.items?.length ?? 0) },
    ],
    columns: [
      { label: 'Nummer', value: (item: any) => item.article_no },
      { label: 'Artikel', value: (item: any) => item.name },
      { label: 'Einheit', value: (item: any) => item.unit },
      { label: 'Soll', value: (item: any) => printQuantity(item.expected_qty), align: 'right' },
      { label: 'Gezählt', value: () => '', align: 'right' },
      { label: 'Bemerkung', value: () => '' },
    ],
    rows: count.items ?? [],
    signatures: ['Datum, Zähler/in', 'Datum, zweite Zählperson', 'Datum, Lagerleitung'],
    note: 'Abweichungen bitte in der Spalte Bemerkung begründen (z. B. Bruch, Fehlbuchung, Fundstück).',
  });
}

/**
 * Inventurabschluss als Nachweis.
 *
 * Anders als die Zählliste enthält er alle Positionen mit Soll, Ist, Differenz
 * und Wert – auch die ohne Abweichung. Ein Abschluss, der nur die Differenzen
 * zeigt, belegt nicht, dass der Rest tatsächlich gezählt wurde.
 */
function printClosingReport(count: any) {
  const items: any[] = count.items ?? [];
  const differences = items.filter((item) => item.counted_qty != null && item.counted_qty !== item.expected_qty);
  const differenceValue = differences.reduce((sum, item) => sum + (item.difference_value ?? 0), 0);
  const uncounted = items.filter((item) => item.counted_qty == null).length;

  printList({
    title: 'Inventurabschluss',
    subtitle: count.name,
    orientation: 'landscape',
    meta: [
      { label: 'Lager', value: count.warehouse_name },
      { label: 'Begonnen', value: formatDateTime(count.started_at) },
      { label: 'Abgeschlossen', value: count.closed_at ? formatDateTime(count.closed_at) : '–' },
      { label: 'Positionen', value: String(items.length) },
      { label: 'Differenzen', value: String(differences.length) },
      { label: 'Wertdifferenz', value: formatCurrency(differenceValue) },
    ],
    columns: [
      { label: 'Nummer', value: (item: any) => item.article_no },
      { label: 'Artikel', value: (item: any) => item.name },
      { label: 'Einheit', value: (item: any) => item.unit },
      { label: 'Soll', value: (item: any) => printQuantity(item.expected_qty), align: 'right' },
      { label: 'Gezählt', value: (item: any) => (item.counted_qty == null ? 'nicht gezählt' : printQuantity(item.counted_qty)), align: 'right' },
      {
        label: 'Differenz',
        align: 'right',
        value: (item: any) => {
          if (item.counted_qty == null) return '–';
          const difference = item.counted_qty - item.expected_qty;
          return `${difference > 0 ? '+' : ''}${printQuantity(difference)}`;
        },
        emphasis: (item: any) =>
          item.counted_qty == null ? 'muted' : item.counted_qty === item.expected_qty ? null : 'warning',
      },
      {
        label: 'Wert',
        align: 'right',
        value: (item: any) =>
          item.counted_qty == null || item.counted_qty === item.expected_qty
            ? '–'
            : formatCurrency(item.difference_value ?? 0),
      },
    ],
    rows: items,
    footer: [
      { label: `Summe der Wertdifferenzen (${differences.length} Position(en))`, value: formatCurrency(differenceValue) },
    ],
    note: uncounted
      ? `${uncounted} Position(en) wurden nicht gezählt und beim Abschluss unverändert übernommen.`
      : 'Alle Positionen wurden gezählt. Die Differenzen sind als Bestandsbewegung „Inventur“ gebucht.',
    signatures: ['Datum, Lagerleitung', 'Datum, Geschäftsführung'],
  });
}

/** Zählliste: Mengen erfassen, Differenzen sehen, abschließen. */
function CountSheetModal({
  countId,
  canCount,
  onClose,
  onChanged,
}: {
  countId: number;
  canCount: boolean;
  onClose: () => void;
  onChanged: () => void;
}) {
  const { notify } = useToast();
  const [onlyOpen, setOnlyOpen] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  const count = useAsync(() => api.get(`/api/stocktaking/counts/${countId}`), [countId]);
  const [drafts, setDrafts] = useState<Record<number, string>>({});

  const items: any[] = count.data?.items ?? [];
  const isOpen = count.data?.status === 'open';
  const visible = onlyOpen ? items.filter((item) => item.counted_qty == null) : items;

  const differences = items.filter((item) => item.counted_qty != null && item.counted_qty !== item.expected_qty);
  const differenceValue = differences.reduce((sum, item) => sum + (item.difference_value ?? 0), 0);

  /** Menge speichern – beim Verlassen des Feldes, nicht bei jedem Tastendruck. */
  const saveItem = async (item: any, raw: string) => {
    const value = raw.trim() === '' ? null : Number(raw);
    if (value != null && Number.isNaN(value)) return;
    try {
      await api.patch(`/api/stocktaking/counts/${countId}/items/${item.id}`, { countedQty: value });
      count.reload();
      onChanged();
    } catch (caught) {
      notify(caught instanceof Error ? caught.message : 'Speichern fehlgeschlagen.', 'error');
    }
  };

  const close = async () => {
    if (!window.confirm(`Inventur abschließen? ${differences.length} Differenz(en) werden als Bestandsbewegung gebucht.`)) {
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const result = await api.post(`/api/stocktaking/counts/${countId}/close`);
      notify(`Inventur abgeschlossen – ${result.differences} Differenz(en) gebucht.`, 'success');
      onChanged();
      onClose();
    } catch (caught) {
      setError(caught);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      title={count.data ? `${count.data.name} · ${count.data.warehouse_name}` : 'Zählliste'}
      size="xwide"
      onClose={onClose}
      footer={
        <>
          <span className="small subtle" style={{ marginRight: 'auto' }}>
            {items.filter((item) => item.counted_qty != null).length} von {items.length} gezählt ·{' '}
            {differences.length} Differenz(en) · Wert {formatCurrency(differenceValue)}
          </span>
          {count.data && (
            <button
              className="btn"
              onClick={() => (isOpen ? printCountSheet(count.data) : printClosingReport(count.data))}
            >
              {isOpen ? 'Zählliste drucken' : 'Abschluss drucken'}
            </button>
          )}
          <button className="btn" onClick={onClose}>
            Schließen
          </button>
          {isOpen && canCount && (
            <button className="btn primary" onClick={close} disabled={busy}>
              <IconCheck width={14} height={14} /> Inventur abschließen
            </button>
          )}
        </>
      }
    >
      {count.loading ? (
        <Loading />
      ) : (
        <div className="stack">
          <ErrorMessage error={error} />

          {!isOpen && (
            <div className="alert success">
              <IconCheck width={16} height={16} />
              <span>
                Abgeschlossen am {formatDateTime(count.data.closed_at)} – die Differenzen wurden als
                Bestandsbewegungen gebucht.
              </span>
            </div>
          )}

          {isOpen && (
            <div className="row between">
              <label className="checkbox-row">
                <input type="checkbox" checked={onlyOpen} onChange={(event) => setOnlyOpen(event.target.checked)} />
                Nur noch nicht gezählte
              </label>
              <span className="small subtle">
                Nicht gezählte Positionen bleiben beim Abschluss unangetastet.
              </span>
            </div>
          )}

          <div className="table-wrap">
            <table className="data">
              <thead>
                <tr>
                  <th>Artikel</th>
                  <th className="num">Soll</th>
                  <th className="num" style={{ width: 130 }}>
                    Gezählt
                  </th>
                  <th className="num">Differenz</th>
                  <th className="num">Wert</th>
                </tr>
              </thead>
              <tbody>
                {visible.map((item) => {
                  const counted = item.counted_qty;
                  const difference = counted == null ? null : counted - item.expected_qty;
                  return (
                    <tr key={item.id}>
                      <td>
                        <strong>{item.name}</strong>
                        <div className="small subtle mono">{item.article_no}</div>
                      </td>
                      <td className="num">
                        {formatNumber(item.expected_qty, 2)} <span className="small subtle">{item.unit}</span>
                      </td>
                      <td className="num">
                        {isOpen && canCount ? (
                          <input
                            type="number"
                            step="0.01"
                            defaultValue={counted ?? ''}
                            key={`${item.id}-${counted}`}
                            onBlur={(event) => {
                              if (event.target.value === String(counted ?? '')) return;
                              void saveItem(item, event.target.value);
                            }}
                            onKeyDown={(event) => {
                              if (event.key === 'Enter') (event.target as HTMLInputElement).blur();
                            }}
                            style={{ textAlign: 'right' }}
                          />
                        ) : (
                          <span>{counted == null ? '–' : formatNumber(counted, 2)}</span>
                        )}
                      </td>
                      <td className="num">
                        {difference == null ? (
                          <span className="subtle">–</span>
                        ) : difference === 0 ? (
                          <Badge variant="success">stimmt</Badge>
                        ) : (
                          <strong style={{ color: difference < 0 ? 'var(--danger-text)' : 'var(--warning-text)' }}>
                            {difference > 0 ? '+' : ''}
                            {formatNumber(difference, 2)}
                          </strong>
                        )}
                      </td>
                      <td className="num small subtle">
                        {difference == null || difference === 0 ? '–' : formatCurrency(item.difference_value ?? 0)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {visible.length === 0 && (
            <EmptyState title="Alles gezählt" hint="Es sind keine offenen Positionen mehr vorhanden." />
          )}
        </div>
      )}
    </Modal>
  );
}
